/**
 * Framebuffer objects (W2-01a preview-gl; ARCHITECTURE.md §7.3 step 5, rendering.md R§3.7, R§8, R§9).
 *
 * - `ColorTarget`: an RGBA8 texture with its framebuffer, LINEAR + CLAMP_TO_EDGE (effects sample it as
 *   `screenTexture`; paint buffers composite it). Two of them form the ping-pong pair of the effect chain.
 * - `MsaaTarget`: a multisampled RGBA8 renderbuffer. The Phira "MSAA" player setting renders the frame into it and
 *   resolves it with `blitFramebuffer`; the canvas context itself is always created without antialiasing, so
 *   switching the setting never needs a new context.
 * Targets are created lazily, resized only when the size changes, and re-created after a context loss.
 */

export class ColorTarget {
  private readonly gl: WebGL2RenderingContext;
  tex: WebGLTexture | null = null;
  fbo: WebGLFramebuffer | null = null;
  w = 0;
  h = 0;

  constructor(gl: WebGL2RenderingContext) {
    this.gl = gl;
  }

  /** Make the target `w` x `h` (no-op when it already is). Returns true when the storage was (re)allocated. */
  ensure(w: number, h: number): boolean {
    const gl = this.gl;
    const W = Math.max(1, Math.round(w));
    const H = Math.max(1, Math.round(h));
    if (this.tex !== null && this.w === W && this.h === H) return false;
    if (this.tex === null) this.tex = gl.createTexture();
    if (this.fbo === null) this.fbo = gl.createFramebuffer();
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, W, H, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.tex, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    this.w = W;
    this.h = H;
    return true;
  }

  dispose(): void {
    this.gl.deleteFramebuffer(this.fbo);
    this.gl.deleteTexture(this.tex);
    this.fbo = null;
    this.tex = null;
    this.w = 0;
    this.h = 0;
  }
}

export class MsaaTarget {
  private readonly gl: WebGL2RenderingContext;
  rb: WebGLRenderbuffer | null = null;
  fbo: WebGLFramebuffer | null = null;
  w = 0;
  h = 0;
  samples = 0;

  constructor(gl: WebGL2RenderingContext) {
    this.gl = gl;
  }

  ensure(w: number, h: number, samples: number): boolean {
    const gl = this.gl;
    const W = Math.max(1, Math.round(w));
    const H = Math.max(1, Math.round(h));
    if (this.rb !== null && this.w === W && this.h === H && this.samples === samples) return false;
    if (this.rb === null) this.rb = gl.createRenderbuffer();
    if (this.fbo === null) this.fbo = gl.createFramebuffer();
    gl.bindRenderbuffer(gl.RENDERBUFFER, this.rb);
    gl.renderbufferStorageMultisample(gl.RENDERBUFFER, samples, gl.RGBA8, W, H);
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fbo);
    gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.RENDERBUFFER, this.rb);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.bindRenderbuffer(gl.RENDERBUFFER, null);
    this.w = W;
    this.h = H;
    this.samples = samples;
    return true;
  }

  dispose(): void {
    this.gl.deleteFramebuffer(this.fbo);
    this.gl.deleteRenderbuffer(this.rb);
    this.fbo = null;
    this.rb = null;
    this.w = 0;
    this.h = 0;
    this.samples = 0;
  }
}

/**
 * Copy the colour of one framebuffer into another of the same size (MSAA resolve, ping-pong pre-copy). The bound
 * draw framebuffer afterwards is undefined for callers: they rebind through GlState.
 */
export function blitColor(
  gl: WebGL2RenderingContext,
  src: WebGLFramebuffer | null,
  dst: WebGLFramebuffer | null,
  w: number,
  h: number,
): void {
  gl.bindFramebuffer(gl.READ_FRAMEBUFFER, src);
  gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, dst);
  gl.blitFramebuffer(0, 0, w, h, 0, 0, w, h, gl.COLOR_BUFFER_BIT, gl.NEAREST);
  gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
  gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
}

/** Two equal-sized colour targets used alternately as source and destination of a pass chain. */
export class PingPong {
  readonly a: ColorTarget;
  readonly b: ColorTarget;

  constructor(gl: WebGL2RenderingContext) {
    this.a = new ColorTarget(gl);
    this.b = new ColorTarget(gl);
  }

  ensure(w: number, h: number): void {
    this.a.ensure(w, h);
    this.b.ensure(w, h);
  }

  /** The member that is not `t`. */
  other(t: ColorTarget): ColorTarget {
    return t === this.a ? this.b : this.a;
  }

  dispose(): void {
    this.a.dispose();
    this.b.dispose();
  }
}
